import styled from "@emotion/styled";
import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { ActionButton, colors } from "@zfp-akademie-a-s/zfp-design";
import { InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Popper } from "Components/_Shared/Popper";
import { useGetModelingRecapitulation } from "Infrastructure/ApiZfp/AnalyzerApi";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency, formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, Fragment, useState } from "react";
import { useParams } from "react-router-dom";

const StyledWrapper = styled(Box)`
   ${({ theme }) => `
    width: ${theme.spacing(100)};
    background: ${colors.white};
    border-radius: ${theme.spacing(Radius.L)};
  `};
`;

export const RecapitulationTooltip: FC = () => {
  const theme = useTheme();
  const { t } = useResource();

  const [open, setOpen] = useState(false);

  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { data: recapitulation } = useGetModelingRecapitulation(analysisId, versionId, {
    query: { enabled: open },
  });

  return (
    <Popper
      isOpen={open}
      noPadding
      placement="bottom"
      onOpen={() => {
        setOpen(true);
      }}
      btnEl={
        <ActionButton label={t(Resources.AnalysisDetail.Modeling.RemainingForRedistributionDetail)} icon={<InfoIcon />} tone="neutral" />
      }
    >
      <StyledWrapper boxShadow={2}>
        <Stack spacing={Spacing.M} paddingY={Spacing.L} paddingX={Spacing.XL}>
          <Typography variant="body2" fontWeight={500} color="text.primary">
            {t(Resources.AnalysisDetail.Modeling.AvailableMonthlyResources)}
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(Resources.AnalysisDetail.Modeling.RemainingForRedistribution)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {formatCurrencyWithoutCode(recapitulation?.availableAmount)}&nbsp;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(Resources.AnalysisDetail.Modeling.FromTotal)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {formatCurrencyWithoutCode(recapitulation?.totalAvailableAmount)}&nbsp;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Divider />
        <Stack spacing={Spacing.M} paddingY={Spacing.L} paddingX={Spacing.XL}>
          <Typography variant="body2" fontWeight={500} color="text.primary">
            {t(Resources.AnalysisDetail.Modeling.ExistingFinancialProducts)}
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(Resources.AnalysisDetail.Modeling.RemainingForRedistribution)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {formatCurrencyWithoutCode(recapitulation?.availableAssetAmount)}&nbsp;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.Common.CZKSuffix)}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t(Resources.AnalysisDetail.Modeling.FromTotal)}
            </Typography>
            <Box display="flex">
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {formatCurrencyWithoutCode(recapitulation?.totalAvailableAssetAmount)}&nbsp;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.Common.CZKSuffix)}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Stack
          spacing={Spacing.L}
          paddingY={Spacing.L}
          paddingX={Spacing.XL}
          sx={{ background: colors.backgroundSecondary, borderRadius: theme.spacing(Radius.L) }}
        >
          {recapitulation?.assetsRecapitulation.map((item, index) => (
            <Fragment key={`${item.labels?.name ?? "item"}-${index}`}>
              <Stack>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={500}>
                    {item.labels?.name}
                  </Typography>
                  <Box display="flex">
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {formatCurrencyWithoutCode(item.availableAssetAmount.value)}&nbsp;
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {!!item.labels?.clientLabel && `${item.labels.clientLabel} • `}
                  {!!item.labels?.institutionLabel && `${item.labels.institutionLabel} • `}
                  {`${t(Resources.AnalysisDetail.Modeling.FromTotal)} ${formatCurrency(item.totalAvailableAssetAmount.value, item.totalAvailableAssetAmount.currencyCode)}`}
                </Typography>
              </Stack>
              {index !== recapitulation.assetsRecapitulation.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Stack>
      </StyledWrapper>
    </Popper>
  );
};
